import React from 'react';
import { EnrichedRiskArea } from '../../types/prevention';
import { RISK_LEVEL_CONFIG, STATUS_CONFIG, getRiskBand } from '../RiskAreasWidget';
import { ChevronRight } from 'lucide-react';
import { SifBadge, DataConfidenceBadge, ScheduledDateBadge, FreshnessBadge, hasDataGap } from './shared';
interface Props {
  areas: EnrichedRiskArea[];
  onSelect: (area: EnrichedRiskArea) => void;
}
export function VariantATable({
  areas,
  onSelect
}: Props) {
  if (areas.length === 0) return <div className="p-8 text-center text-gray-500" data-id="element-2815">No activities found</div>;
  return <div className="w-full overflow-x-auto" data-id="element-2816">
      <table className="w-full text-left border-collapse" data-id="element-2817">
        <thead data-id="element-2818">
          <tr className="border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50" data-id="element-2819">
            <th className="px-4 py-3" data-id="element-2820">Risk Score</th>
            <th className="px-4 py-3" data-id="element-2821">Location</th>
            <th className="px-4 py-3" data-id="element-2822">Date</th>
            <th className="px-4 py-3" data-id="element-2823">Status</th>
            <th className="px-4 py-3 text-center relative group/sifth" data-id="element-2824">
              <span className="underline decoration-dotted decoration-gray-400 underline-offset-2 cursor-help" data-id="element-2825">
                SIF
              </span>
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 z-50 hidden group-hover/sifth:block w-56 bg-gray-900 text-white text-xs font-normal normal-case tracking-normal rounded-lg px-3 py-2 shadow-xl pointer-events-none text-left" data-id="element-2826">
                <span className="font-bold" data-id="element-2827">SIF</span> — Serious Injury or
                Fatality. Flags activities with potential for life-threatening
                outcomes.
              </div>
            </th>
            <th className="px-4 py-3" data-id="element-2828">Freshness</th>
            <th className="px-4 py-3" data-id="element-2829">Data Quality</th>
            <th className="px-4 py-3" data-id="element-2830"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100" data-id="element-2831">
          {areas.map(area => {
          const config = RISK_LEVEL_CONFIG[area.riskLevel];
          const statusConfig = STATUS_CONFIG[area.status];
          const StatusIcon = statusConfig.icon;
          const band = getRiskBand(area.riskScore);
          const dataGap = hasDataGap(area);
          let rowBg = 'hover:bg-gray-50 cursor-pointer transition-colors';
          if (area.sifPotential) {
            rowBg = 'cursor-pointer transition-colors bg-red-50/60 hover:bg-red-50 border-l-4 border-red-700';
          } else if (dataGap) {
            rowBg = 'cursor-pointer transition-colors bg-amber-50/30 hover:bg-amber-50/50 border-l-4 border-dashed border-amber-400';
          } else if (area.status === 'closed') {
            rowBg += ' bg-emerald-50/30 opacity-75 border-l-4 border-transparent';
          } else if (area.status === 'measures_taken') {
            rowBg += ' bg-amber-50/30 border-l-4 border-transparent';
          } else {
            rowBg += ' border-l-4 border-transparent';
          }
          return <tr key={area.id} onClick={() => onSelect(area)} className={rowBg} data-id="element-2832">
                <td className="px-4 py-3" data-id="element-2833">
                  <div className="flex items-center gap-2" data-id="element-2834">
                    <div className={`w-10 py-1 rounded ${config.bgColor} flex flex-col items-center justify-center`} data-id="element-2835">
                      <span className={`text-[10px] font-bold ${config.color}`} data-id="element-2836">
                        {band.short}
                      </span>
                    </div>
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${config.bgColor} ${config.color}`} data-id="element-2837">
                      {area.riskScore}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3" data-id="element-2838">
                  <div className="text-sm font-semibold text-gray-900" data-id="element-2839">
                    {area.project}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-2840">
                    {area.floor} · {area.location}
                  </div>
                </td>
                <td className="px-4 py-3" data-id="element-2841">
                  <ScheduledDateBadge date={area.scheduledDate} data-id="element-2842" />
                </td>
                <td className="px-4 py-3" data-id="element-2843">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full ${statusConfig.bgColor} ${statusConfig.color}`} data-id="element-2844">
                    <StatusIcon size={10} data-id="element-2845" />
                    {statusConfig.label}
                  </span>
                </td>
                <td className="px-4 py-3 text-center" data-id="element-2846">
                  {area.sifPotential ? <SifBadge data-id="element-2847" /> : <span className="text-gray-300" data-id="element-2848">-</span>}
                </td>
                <td className="px-4 py-3" data-id="element-2849">
                  <FreshnessBadge lastUpdated={area.lastUpdated} data-id="element-2850" />
                </td>
                <td className="px-4 py-3" data-id="element-2851">
                  <DataConfidenceBadge confidence={area.dataConfidence} data-id="element-2852" />
                </td>
                <td className="px-4 py-3 text-right" data-id="element-2853">
                  <ChevronRight size={16} className="text-gray-300 inline-block" data-id="element-2854" />
                </td>
              </tr>;
        })}
        </tbody>
      </table>
    </div>;
}